import React, { useState } from 'react';
import { Layers, BarChart2, PieChart } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детализации затрат на материалы и прочие расходы
 */
const MaterialBreakdown = ({ materialBreakdown, results }) => {
  const [chartType, setChartType] = useState('pie');

  if (!results || !materialBreakdown || materialBreakdown.length === 0) {
    return null;
  }

  // Получаем символ валюты
  let currencySymbol = '₽';
  switch(results.currency) {
    case 'USD':
      currencySymbol = '$';
      break;
    case 'EUR':
      currencySymbol = '€';
      break;
    case 'CNY':
      currencySymbol = '¥';
      break;
    default:
      currencySymbol = '₽';
  }

  // Форматирование валюты с выбранным символом
  const formatCurrencyWithSymbol = (value) => {
    return `${formatNumber(value, 2)} ${currencySymbol}`;
  };

  // Цвета для графика
  const colors = [
    'rgba(79, 70, 229, 0.8)', // indigo
    'rgba(45, 212, 191, 0.8)', // teal
    'rgba(234, 88, 12, 0.8)',  // orange
    'rgba(16, 185, 129, 0.8)', // green
    'rgba(139, 92, 246, 0.8)', // purple
    'rgba(239, 68, 68, 0.8)',  // red
  ];

  // Создаем данные для диаграммы
  const chartData = materialBreakdown.map((item, index) => ({
    ...item,
    color: colors[index % colors.length]
  }));

  // Общая сумма затрат (для расчета процентов)
  const totalCost = results.totalCostPrice;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-teal-500" />
          Структура себестоимости
        </h3>
        
        {/* Переключатель типа диаграммы */}
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setChartType('pie')}
            className={`px-3 py-1 text-xs rounded-md ${
              chartType === 'pie' 
                ? 'bg-white dark:bg-gray-600 shadow-sm' 
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            <PieChart className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`px-3 py-1 text-xs rounded-md ${
              chartType === 'bar' 
                ? 'bg-white dark:bg-gray-600 shadow-sm' 
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            <BarChart2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Визуализация структуры затрат */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
        {/* Диаграмма */}
        <div className="flex justify-center items-center">
          {chartType === 'pie' ? (
            <svg width="200" height="200" viewBox="0 0 100 100">
              {/* Круговая диаграмма */}
              {chartData.map((item, index, array) => {
                // Расчет углов для сегментов круговой диаграммы
                let previousPercents = 0;
                for (let i = 0; i < index; i++) {
                  previousPercents += array[i].percent;
                }
                
                const startAngle = (previousPercents / 100) * 2 * Math.PI - Math.PI/2;
                const endAngle = ((previousPercents + item.percent) / 100) * 2 * Math.PI - Math.PI/2;
                
                // Координаты для пути
                const x1 = 50 + 40 * Math.cos(startAngle);
                const y1 = 50 + 40 * Math.sin(startAngle);
                const x2 = 50 + 40 * Math.cos(endAngle);
                const y2 = 50 + 40 * Math.sin(endAngle);
                
                // Флаг большой дуги (большой дуги, если > 180 градусов)
                const largeArcFlag = item.percent > 50 ? 1 : 0;
                
                // Создаем путь для сегмента
                const path = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
                
                return (
                  <path 
                    key={index} 
                    d={path} 
                    fill={item.color} 
                    stroke="#fff" 
                    strokeWidth="0.5"
                  />
                );
              })}
            </svg>
          ) : (
            <svg width="280" height="200" viewBox="0 0 280 200">
              {/* Горизонтальная столбчатая диаграмма */}
              {chartData.map((item, index) => {
                const barHeight = 20;
                const spacing = 10;
                const y = index * (barHeight + spacing) + 10;
                const width = (item.percent / 100) * 240;
                
                return (
                  <g key={index}>
                    <rect 
                      x="30" 
                      y={y} 
                      width={width} 
                      height={barHeight} 
                      fill={item.color} 
                      rx="2" 
                      ry="2"
                    />
                    <text 
                      x={35} 
                      y={y + barHeight/2 + 5} 
                      fontSize="9" 
                      fontWeight="bold" 
                      fill="#fff"
                    >
                      {item.name} ({item.percent.toFixed(1)}%)
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
        </div>
        
        {/* Таблица с данными */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 font-semibold">Категория</th>
                <th className="px-3 py-2 font-semibold text-right">Сумма</th>
                <th className="px-3 py-2 font-semibold text-right">Доля</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {chartData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-sm mr-2" style={{backgroundColor: item.color}}></div>
                      {item.name}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right font-medium">
                    {formatCurrencyWithSymbol(item.value)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {formatNumber(item.percent, 1)}%
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                <td className="px-3 py-2">Всего</td>
                <td className="px-3 py-2 text-right">{formatCurrencyWithSymbol(totalCost)}</td>
                <td className="px-3 py-2 text-right">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Информационный блок */}
      <div className="mt-4 bg-teal-50 dark:bg-teal-900/20 p-3 rounded-lg border border-teal-100 dark:border-teal-800">
        <div className="flex items-start">
          <Layers className="w-5 h-5 text-teal-600 dark:text-teal-400 mr-3 mt-1 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-teal-700 dark:text-teal-300 mb-1">
              Как снизить стоимость 3D-печати?
            </h4>
            <p className="text-xs text-teal-600 dark:text-teal-400">
              Структура затрат поможет определить, на чем можно сэкономить. Если большую часть составляет стоимость материала, рассмотрите возможность использования более доступного филамента или оптимизации модели (уменьшение размера, плотности заполнения). Если большие затраты на амортизацию, возможно, стоит увеличить загрузку принтера или пересмотреть срок амортизации.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialBreakdown;